// The tokens of a formula, for colouring and completion: the same rules as the server lexer
// (`server/crm/filter_tree/formula.py`, `lex`). The server parses and checks; the front only
// colours what it reads and never rejects a character.

export type TokenKind = "field" | "string" | "number" | "duration" | "ident" | "op" | "lparen" | "rparen" | "comma" | "error";

export type Token = {
    kind: TokenKind;
    text: string;
    start: number;
    end: number;
    /** A field or a text whose closing character is missing. */
    open?: boolean;
    /** The inside of a field (`Opportunity.Created date`). */
    content?: string;
};

const UNIT_NAMES: Record<string, "days" | "weeks" | "months" | "years"> = {
    day: "days",
    days: "days",
    jour: "days",
    jours: "days",
    week: "weeks",
    weeks: "weeks",
    semaine: "weeks",
    semaines: "weeks",
    month: "months",
    months: "months",
    mois: "months",
    year: "years",
    years: "years",
    an: "years",
    ans: "years",
    annee: "years",
    annees: "years",
    année: "years",
    années: "years"
};

/** The unit a word names (`days` for « jours »), or undefined. */
export function unitOf(word: string): "days" | "weeks" | "months" | "years" | undefined {
    const key = word.toLowerCase();
    return Object.prototype.hasOwnProperty.call(UNIT_NAMES, key) ? UNIT_NAMES[key] : undefined;
}

const OPERATORS = new Set(["+", "-", "−", "*", "×", "/", "÷"]);
// As the server's `[^\W\d]\w*`: a letter (or a number that is not a digit, ², Ⅳ) or `_`, then letters,
// numbers and `_`.
const WORD = /[\p{L}\p{Nl}\p{No}_][\p{L}\p{N}_]*/uy;
const NUMBER = /\d+(?:\.\d+)?/y;
const SPACES = /\s*/y;

function matchAt(pattern: RegExp, text: string, index: number): string | null {
    pattern.lastIndex = index;
    const match = pattern.exec(text);
    return match ? match[0] : null;
}

/** Whether `char` starts a word (a function name, a unit): a letter or `_`. */
export function isWordStart(char: string): boolean {
    return /[\p{L}\p{Nl}\p{No}_]/u.test(char);
}

export function lex(text: string): Token[] {
    const tokens: Token[] = [];
    let i = 0;
    while (i < text.length) {
        const char = text[i];
        if (/\s/.test(char)) {
            i += 1;
            continue;
        }
        if (char === "[") {
            const close = text.indexOf("]", i + 1);
            const end = close < 0 ? text.length : close + 1;
            tokens.push({ kind: "field", text: text.slice(i, end), start: i, end, open: close < 0, content: text.slice(i + 1, close < 0 ? text.length : close) });
            i = end;
            continue;
        }
        if (char === '"') {
            let j = i + 1;
            while (j < text.length && text[j] !== '"') j += text[j] === "\\" && j + 1 < text.length ? 2 : 1;
            const end = Math.min(j + 1, text.length);
            tokens.push({ kind: "string", text: text.slice(i, end), start: i, end, open: j >= text.length });
            i = end;
            continue;
        }
        const number = matchAt(NUMBER, text, i);
        if (number) {
            const afterNumber = i + number.length;
            const spaces = matchAt(SPACES, text, afterNumber) ?? "";
            const word = matchAt(WORD, text, afterNumber + spaces.length);
            if (word && unitOf(word)) {
                const end = afterNumber + spaces.length + word.length;
                tokens.push({ kind: "duration", text: text.slice(i, end), start: i, end });
                i = end;
            } else {
                tokens.push({ kind: "number", text: number, start: i, end: afterNumber });
                i = afterNumber;
            }
            continue;
        }
        const word = matchAt(WORD, text, i);
        if (word) {
            tokens.push({ kind: "ident", text: word, start: i, end: i + word.length });
            i += word.length;
            continue;
        }
        const kind: TokenKind = OPERATORS.has(char) ? "op" : char === "(" ? "lparen" : char === ")" ? "rparen" : char === "," || char === ";" ? "comma" : "error";
        tokens.push({ kind, text: char, start: i, end: i + 1 });
        i += 1;
    }
    return tokens;
}

/** The scope a field's text names (its first segment), or the tested record's (the last scope). */
export function fieldScope<S extends { name: string; index: number }>(content: string, scopes: S[]): S | undefined {
    if (!scopes.length) return undefined;
    const canonical = /^\$(\d+)\./.exec(content.trim());
    if (canonical) return scopes[Number(canonical[1])];
    const lower = content.trim().toLowerCase();
    const named = [...scopes].sort((a, b) => b.name.length - a.name.length).find(scope => lower.startsWith(`${scope.name.toLowerCase()}.`));
    return named ?? scopes[scopes.length - 1];
}
